/**
 * PersonMonthDialog — what a report count expands into.
 * @file src/web/features/reports/components/PersonMonthDialog.tsx
 *
 * @llm-rule WHEN: Someone clicks a flag or task count on the monthly report
 * @llm-rule NOTE: Opens on the tab that was clicked — clicking Flags should
 *   not make you hunt for the flags. Fetches on open so a long report doesn't
 *   pull every person's detail up front.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Skeleton,
  Tabs,
  TabsContent,
  toast,
} from '@bloomneo/uikit';
import { AlarmClock, Flag, MapPin, Plane, ShieldOff } from 'lucide-react';
import { useAppRequest } from '../../../shared/hooks/useAppApi';
import { rankOf, ROLE_LABEL, type OrgRole } from '../../../shared/domain';
import { useAuth } from '../../auth';
import { FLAG_LABEL, FLAG_RULE, type FlagCode } from '../../../shared/flags';
import { OFFSITE_REASON_LABEL } from '../../../shared/domain';
import { PersonAvatar } from '../../../shared/components/presence';

interface FlagRow {
  id: string;
  code: FlagCode | null;
  body: string;
  day: string;
  status: string;
  waivedAt: string | null;
  waiveReason: string | null;
  waivedBy: { id: string; name: string | null; orgRole: OrgRole } | null;
}

interface TaskRow {
  id: string;
  title: string;
  description: string | null;
  dueAt: string;
  status: string;
  completedAt: string | null;
  closedLate: boolean;
  delayReason: string | null;
  assignedBy: { id: string; name: string | null; orgRole: OrgRole } | null;
}

interface LeaveRow {
  id: string;
  type: string;
  startDate: string;
  endDate: string;
  days: number;
  reason: string;
}

interface OffSiteRow {
  id: string;
  day: string;
  clockInAt: string | null;
  clockOutAt: string | null;
  reason: string | null;
  note: string | null;
  distanceM: number | null;
  nearest: string | null;
}

interface Detail {
  person: {
    id: string;
    name: string | null;
    orgRole: OrgRole;
    title: string | null;
    initials: string | null;
    /** Enrolment reference, base64 JPEG data URL. */
    faceImage?: string | null;
  };
  flags: FlagRow[];
  tasks: TaskRow[];
  leave: LeaveRow[];
  offSite: OffSiteRow[];
  /** Who cleared flags for this person this month, and how many each. */
  waivers?: {
    total: number;
    byManager: number;
    byHead: number;
    by: Array<{ id: string; name: string | null; orgRole: OrgRole; count: number }>;
  };
}

function when(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function dayLabel(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

type SectionKey = 'flags' | 'waived' | 'tasks' | 'offsite' | 'leave';

/**
 * The sections, in the order they are read.
 *
 * Active and waived flags are separate on purpose. Counting them together made
 * "Flags 3" mean nothing — it could be three problems or three already
 * forgiven. Active is the number that needs attention; waived is the audit
 * trail beside it.
 */
const SECTIONS: Array<{ key: SectionKey; label: string; icon: typeof Flag }> = [
  { key: 'flags', label: 'Active flags', icon: Flag },
  { key: 'waived', label: 'Waived', icon: ShieldOff },
  { key: 'tasks', label: 'Tasks', icon: AlarmClock },
  { key: 'offsite', label: 'Off-site', icon: MapPin },
  { key: 'leave', label: 'Leave', icon: Plane },
];

/** A flag counts as waived once someone has signed it off; everything else is active. */
const isWaived = (f: FlagRow) => Boolean(f.waivedAt);

function countOf(key: SectionKey, detail: Detail): number {
  if (key === 'flags') return detail.flags.filter((f) => !isWaived(f)).length;
  if (key === 'waived') return detail.flags.filter(isWaived).length;
  if (key === 'tasks') return detail.tasks.length;
  if (key === 'offsite') return detail.offSite?.length ?? 0;
  return detail.leave.length;
}

export function PersonMonthDialog({
  personId,
  personName,
  tab,
  year,
  month,
  monthLabel,
  onClose,
}: {
  personId: string | null;
  personName: string;
  tab: 'flags' | 'tasks' | 'offsite';
  year: number;
  month: number;
  monthLabel: string;
  onClose: () => void;
}) {
  const { request } = useAppRequest();
  const [detail, setDetail] = useState<Detail | null>(null);
  /** Which section is showing. Seeded from `tab` so opening on "tasks" still works. */
  const [active, setActive] = useState<SectionKey>(tab);

  // Oversight of a Manager's waivers is for the ranks above them.
  const { user } = useAuth();
  const canSeeWaivers = rankOf((user?.orgRole ?? 'EMPLOYEE') as OrgRole) > rankOf('MANAGER');

  useEffect(() => {
    setActive(tab);
  }, [tab, personId]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!personId) return;
    setLoading(true);
    setDetail(null);

    const result = await request<Detail>(
      `/api/report/monthly/${personId}?year=${year}&month=${month}`,
    );
    if (result.ok) setDetail(result.data);
    else toast.error(result.error);
    setLoading(false);
  }, [request, personId, year, month]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Dialog open={personId !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-h-[85vh] w-[calc(100vw-1.5rem)] max-w-2xl overflow-y-auto sm:w-full"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2.5">
            {detail && <PersonAvatar person={detail.person} size={28} />}
            {detail?.person.name ?? personName}
          </DialogTitle>
          <DialogDescription>
            {detail?.person.title || (detail && ROLE_LABEL[detail.person.orgRole])} · {monthLabel}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <Skeleton className="h-64 w-full rounded-xl" />
        ) : !detail ? (
          <p className="py-8 text-center text-[13px] text-ink-faint">Nothing to show.</p>
        ) : (
          <Tabs value={active} onValueChange={(v) => setActive(v as SectionKey)}>
            {/* Who granted this person a clean record, and how much of it.
                Shown only to Heads and the Company Admin: a Manager seeing a
                tally of their own waivers adds nothing, and the point of the
                band is oversight of the Manager. */}
            {canSeeWaivers && detail.waivers && detail.waivers.total > 0 && (
              <div className="mb-2.5 rounded-[10px] border border-[var(--state-warn-border)] bg-[var(--state-warn-bg)] px-3 py-2">
                <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-soft">
                  {detail.waivers.total} flag{detail.waivers.total === 1 ? '' : 's'} waived this
                  month
                </div>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11.5px] text-ink-soft">
                  <span>
                    <strong className="text-ink">{detail.waivers.byManager}</strong> by a Manager
                  </span>
                  <span>
                    <strong className="text-ink">{detail.waivers.byHead}</strong> by a Head
                  </span>
                </div>
                {detail.waivers.by.length > 0 && (
                  <div className="mt-1 text-[11px] text-ink-ghost">
                    {detail.waivers.by
                      .map((w) => `${w.name ?? 'Someone'} (${ROLE_LABEL[w.orgRole]}) · ${w.count}`)
                      .join('  ·  ')}
                  </div>
                )}
              </div>
            )}

            {/* Buttons that WRAP rather than a scrolling tab strip. Four tabs
                with icons and counts overflowed a phone, and a horizontal
                scroller hides whichever section you were not already looking
                at. These reflow onto a second row instead. */}
            <div className="flex flex-wrap gap-1.5">
              {SECTIONS.map((section) => {
                const count = countOf(section.key, detail);
                const on = active === section.key;
                return (
                  <button
                    key={section.key}
                    type="button"
                    onClick={() => setActive(section.key)}
                    aria-pressed={on}
                    className={`flex flex-none items-center gap-1.5 rounded-[10px] border px-2.5 py-2 text-[12.5px] font-medium transition-colors ${
                      on
                        ? 'border-ink bg-ink text-paper'
                        : 'border-line bg-paper text-ink-soft hover:bg-paper-warm'
                    }`}
                  >
                    <section.icon className="size-3.5 flex-none" strokeWidth={1.9} />
                    <span>{section.label}</span>
                    <span
                      className={`inline-flex min-w-[18px] items-center justify-center rounded-full px-1.5 text-[10.5px] font-semibold tabular-nums ${
                        on
                          ? 'bg-paper/25 text-paper'
                          : // Only ACTIVE flags are alarming. A waived count in
                            // red would read as a problem when it is the opposite.
                            section.key === 'flags' && count > 0
                            ? 'bg-[var(--state-bad-bg)] text-flag'
                            : 'bg-paper-sunk text-ink-faint'
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* ---- Active flags ---- */}
            <TabsContent value="flags" className="pt-3">
              <div className="thin-scroll flex max-h-[52vh] flex-col gap-2 overflow-y-auto pr-1">
                {detail.flags.filter((f) => !isWaived(f)).length === 0 ? (
                  <p className="py-8 text-center text-[13px] text-ink-faint">
                    No active flags this month.
                  </p>
                ) : (
                  detail.flags.filter((f) => !isWaived(f)).map((flag) => (
                    <div
                      key={flag.id}
                      className="rounded-xl border border-rule px-3.5 py-2.5"
                      style={flag.waivedAt ? { opacity: 0.65 } : undefined}
                    >
                      <div className="flex flex-wrap items-baseline gap-2">
                        <span className="text-[13px] font-semibold text-ink">
                          {flag.code ? FLAG_LABEL[flag.code] : 'Flag'}
                        </span>
                        <span className="text-[11.5px] text-ink-ghost">{dayLabel(flag.day)}</span>
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] ${
                            flag.waivedAt
                              ? 'bg-[var(--state-ok-bg)] text-appreciation'
                              : 'bg-paper-sunk text-ink-faint'
                          }`}
                        >
                          {flag.waivedAt ? 'waived' : flag.status.toLowerCase()}
                        </span>
                      </div>

                      {flag.code && (
                        <p className="mt-0.5 text-[11px] text-ink-ghost">{FLAG_RULE[flag.code]}</p>
                      )}

                      <p className="mt-1 text-[12.5px] leading-relaxed text-ink-soft">{flag.body}</p>

                      {flag.waiveReason && (
                        <p className="mt-1.5 text-[11.5px] italic text-ink-faint">
                          Waived — “{flag.waiveReason}”
                        </p>
                      )}
                    </div>
                  ))
                )}
              </div>
            </TabsContent>

            {/* ---- Tasks ---- */}
            {/* ---- Waived ---- */}
            <TabsContent value="waived" className="pt-3">
              <div className="thin-scroll flex max-h-[52vh] flex-col gap-2 overflow-y-auto pr-1">
                {detail.flags.filter(isWaived).length === 0 ? (
                  <p className="py-8 text-center text-[13px] text-ink-faint">
                    Nothing has been waived this month.
                  </p>
                ) : (
                  detail.flags.filter(isWaived).map((flag) => (
                    <div key={flag.id} className="rounded-xl border border-rule px-3.5 py-2.5">
                      <div className="flex flex-wrap items-baseline gap-2">
                        <span className="text-[13px] font-semibold text-ink">
                          {flag.code ? FLAG_LABEL[flag.code] : 'Flag'}
                        </span>
                        <span className="text-[11.5px] text-ink-ghost">{dayLabel(flag.day)}</span>
                        <span className="rounded-full bg-[var(--state-ok-bg)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-appreciation">
                          waived
                        </span>
                      </div>

                      <p className="mt-1 text-[12.5px] leading-relaxed text-ink-soft">{flag.body}</p>

                      {/* Who signed it off is the point of this section — a flag
                          cleared by a Manager is a fact about that Manager too. */}
                      <p className="mt-1.5 text-[11.5px] text-ink-faint">
                        {flag.waivedBy
                          ? `${flag.waivedBy.name ?? 'Someone'} (${ROLE_LABEL[flag.waivedBy.orgRole]})`
                          : 'Waiver on record'}
                        {flag.waiveReason ? ` — “${flag.waiveReason}”` : ''}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </TabsContent>

            <TabsContent value="tasks" className="pt-3">
              <div className="thin-scroll flex max-h-[52vh] flex-col gap-2 overflow-y-auto pr-1">
                {detail.tasks.length === 0 ? (
                  <p className="py-8 text-center text-[13px] text-ink-faint">
                    No tasks due this month.
                  </p>
                ) : (
                  detail.tasks.map((task) => (
                    <div key={task.id} className="rounded-xl border border-rule px-3.5 py-2.5">
                      <div className="flex flex-wrap items-baseline gap-2">
                        <span className="text-[13px] font-medium text-ink">{task.title}</span>
                        <span
                          className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em]"
                          style={{
                            background:
                              task.status === 'DONE'
                                ? 'var(--state-ok-bg)'
                                : task.status === 'OVERDUE'
                                  ? 'var(--state-bad-bg)'
                                  : 'var(--state-warn-bg)',
                            color:
                              task.status === 'DONE'
                                ? 'var(--dot-appreciation)'
                                : task.status === 'OVERDUE'
                                  ? 'var(--dot-flag)'
                                  : 'var(--dot-offsite)',
                          }}
                        >
                          {task.status.toLowerCase()}
                        </span>
                      </div>

                      <div className="mt-0.5 text-[11.5px] text-ink-faint">
                        due {when(task.dueAt)}
                        {task.assignedBy && ` · from ${task.assignedBy.name}`}
                        {task.completedAt && (
                          <span className={task.closedLate ? 'text-offsite' : 'text-appreciation'}>
                            {' · '}
                            {task.closedLate ? 'closed late' : 'closed on time'} {when(task.completedAt)}
                          </span>
                        )}
                      </div>

                      {task.description && (
                        <p className="mt-1 text-[12px] leading-relaxed text-ink-soft">
                          {task.description}
                        </p>
                      )}

                      {task.delayReason && (
                        <p className="mt-1.5 rounded-lg bg-[var(--state-warn-bg)] px-2.5 py-1.5 text-[11.5px] leading-relaxed text-ink">
                          <b className="font-semibold">Reason for the delay</b> — “{task.delayReason}”
                        </p>
                      )}
                    </div>
                  ))
                )}
              </div>
            </TabsContent>

            {/* ---- Off-site ---- */}
            <TabsContent value="offsite" className="pt-3">
              <div className="thin-scroll flex max-h-[52vh] flex-col gap-2 overflow-y-auto pr-1">
                {(detail.offSite?.length ?? 0) === 0 ? (
                  <p className="py-8 text-center text-[13px] text-ink-faint">
                    Always clocked in from an office this month.
                  </p>
                ) : (
                  detail.offSite.map((row) => (
                    <div key={row.id} className="rounded-xl border border-rule px-3.5 py-2.5">
                      <div className="flex flex-wrap items-baseline gap-2">
                        <span className="text-[13px] font-medium text-ink">{dayLabel(row.day)}</span>
                        {row.clockInAt && (
                          <span className="tnum text-[11.5px] text-ink-faint">
                            in {when(row.clockInAt).split(', ').pop()}
                            {row.clockOutAt && ` · out ${when(row.clockOutAt).split(', ').pop()}`}
                          </span>
                        )}
                        <span
                          className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em]"
                          style={{ background: 'var(--state-warn-bg)', color: 'var(--dot-offsite)' }}
                        >
                          {row.reason ? OFFSITE_REASON_LABEL[row.reason] ?? row.reason : 'off-site'}
                        </span>
                      </div>

                      {row.distanceM !== null && (
                        <p className="tnum mt-0.5 text-[11.5px] text-ink-ghost">
                          {Math.round(row.distanceM)} m from {row.nearest ?? 'the nearest office'}
                        </p>
                      )}

                      {row.note && (
                        <p className="mt-1 text-[12.5px] leading-relaxed text-ink-soft">
                          “{row.note}”
                        </p>
                      )}
                    </div>
                  ))
                )}
              </div>
            </TabsContent>

            {/* ---- Leave ---- */}
            <TabsContent value="leave" className="pt-3">
              <div className="thin-scroll flex max-h-[52vh] flex-col gap-2 overflow-y-auto pr-1">
                {detail.leave.length === 0 ? (
                  <p className="py-8 text-center text-[13px] text-ink-faint">
                    No approved leave this month.
                  </p>
                ) : (
                  detail.leave.map((row) => (
                    <div key={row.id} className="rounded-xl border border-rule px-3.5 py-2.5">
                      <div className="flex flex-wrap items-baseline gap-2">
                        <span className="text-[13px] font-medium text-ink">
                          {dayLabel(row.startDate)}
                          {row.startDate !== row.endDate && ` → ${dayLabel(row.endDate)}`}
                        </span>
                        <span className="text-[11.5px] text-ink-faint">
                          {row.days} day{row.days === 1 ? '' : 's'}
                        </span>
                        <span className="rounded-full bg-paper-sunk px-2 py-0.5 text-[10px] text-ink-faint">
                          {row.type.toLowerCase()}
                        </span>
                      </div>
                      <p className="mt-1 text-[12.5px] leading-relaxed text-ink-soft">
                        “{row.reason}”
                      </p>
                    </div>
                  ))
                )}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
